/**
 * A camera's settings as the cloud stores them (settings schema v1) and the one rule
 * the settings screen needs from outside React: what to send.
 *
 * The server merges a PATCH into the camera's desired state and bumps its version, so
 * the screen sends only what the operator changed. Sending the whole form would
 * re-assert every field and silently undo a change another operator made a moment
 * earlier on a field this one never touched.
 *
 * Kept free of React and of `window` so it can be checked without a renderer.
 */

export type CameraSettings = {
  video?: {
    main?: VideoStream;
    sub?: VideoStream;
  };
  image?: {
    brightness?: number;
    contrast?: number;
    saturation?: number;
    sharpness?: number;
    mirror?: boolean;
    flip?: boolean;
  };
  dayNight?: {mode?: DayNightMode};
  osd?: {showTime?: boolean; showName?: boolean; text?: string};
  audio?: {enabled?: boolean};
  time?: {timezone?: string; ntpServer?: string};
};

export type VideoStream = {resolution?: string; fps?: number; bitrateKbps?: number};

export type DayNightMode = 'auto' | 'day' | 'night';

/** What the camera says it can do; pickers are built from this. Every field optional:
 *  a camera that reports nothing gets read-only values rather than guessed options. */
export type CameraCapabilities = {
  video?: {
    main?: {resolutions?: string[]; fpsMax?: number};
    sub?: {resolutions?: string[]; fpsMax?: number};
  };
  dayNight?: {modes?: DayNightMode[]};
  audio?: {supported?: boolean};
};

type Json = null | boolean | number | string | Json[] | {[key: string]: Json};

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function diff(base: unknown, draft: unknown): unknown {
  if (isObject(draft)) {
    const b = isObject(base) ? base : {};
    const out: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(draft)) {
      const d = diff(b[key], value);
      if (d !== undefined) {
        out[key] = d;
      }
    }
    return Object.keys(out).length > 0 ? out : undefined;
  }
  return draft === base ? undefined : draft;
}

/** The patch that turns `base` into `draft`, leaf by leaf; `null` when nothing changed. */
export function settingsPatch(
  base: CameraSettings,
  draft: CameraSettings,
): CameraSettings | null {
  const d = diff(base as Json, draft as Json);
  return (d as CameraSettings | undefined) ?? null;
}
